import createRxStore from '..';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  EnvironmentAction,
  EnvironmentActionTypes,
  EnvironmentState,
  Environment,
  EnvironmentConfiguration,
} from '../../types/environment';

export const DEFAULT_LOCAL_SENSEI_API_PORT = 8087;

export const SENSEI_API_DEV_ENVIRONMENT =
  process.env.SENSEI_API_DEV_ENVIRONMENT || '';
export const IS_SENSEI_API_DEV_ENVIRONMENT_LOCKED =
  SENSEI_API_DEV_ENVIRONMENT === Environment.PRODUCTION ||
  SENSEI_API_DEV_ENVIRONMENT === Environment.LOCAL;

export const DEFAULT_CUSTOM_SETTINGS: EnvironmentConfiguration = {
  type: Environment.CUSTOM,
  senseiApiBaseUrl: '',
  coreCucumberBaseUrl: '',
};

function getEnvironmentFromLocalStorage(
  key = 'environment',
): EnvironmentConfiguration {
  // The local dev commands deliberately select their API target. Do this
  // before consulting localStorage so a previous environment-switcher choice
  // cannot silently send a local development session to the wrong backend.
  switch (SENSEI_API_DEV_ENVIRONMENT) {
    case Environment.PRODUCTION:
      return { type: Environment.PRODUCTION };
    case Environment.LOCAL:
      return {
        type: Environment.LOCAL,
        port: `${DEFAULT_LOCAL_SENSEI_API_PORT}`,
      };
  }

  const stringData = window.localStorage.getItem(key);
  if (stringData !== null) {
    let data: any;
    try {
      data = JSON.parse(stringData);
    } catch (err) {
      // If decoding from localStrage failed, then try again with the data removed from localStorage
      window.localStorage.removeItem(key);
      return getEnvironmentFromLocalStorage(key);
    }
    return data;
  } else {
    // Return the default depending on the hostname
    let data: EnvironmentConfiguration;
    switch (window.location.hostname) {
      case 'sensei.density.io':
        data = { type: Environment.PRODUCTION };
        break;
      case 'sensei.density.rodeo':
        data = { type: Environment.STAGING };
        break;
      case 'localhost':
      case '127.0.0.1':
        data = {
          type: Environment.LOCAL,
          port: `${DEFAULT_LOCAL_SENSEI_API_PORT}`,
        };
        break;
      default:
        data = { type: Environment.PRODUCTION };
        break;
    }

    window.localStorage.setItem(key, JSON.stringify(data));
    return data;
  }
}

export function saveEnvironmentToLocalStorage(
  environment: EnvironmentConfiguration,
  key = 'environment',
) {
  window.localStorage.setItem(key, JSON.stringify(environment));
}

export function saveStreamingToLocalStorage(
  streaming: boolean,
  key = 'streaming',
) {
  window.localStorage.setItem(key, `${streaming}`);
}

const initialConfiguration = getEnvironmentFromLocalStorage();

export const initialState: EnvironmentState = {
  workingConfiguration: initialConfiguration,
  configuration: initialConfiguration,
};

function reducer(
  state: EnvironmentState,
  action: EnvironmentAction,
): EnvironmentState {
  switch (action.type) {
    case EnvironmentActionTypes.ENVIRONMENT_SET_CONFIGURATION:
      return { ...state, workingConfiguration: action.configuration };
    case EnvironmentActionTypes.ENVIRONMENT_COMMIT_CONFIGURATION:
      return { ...state, configuration: state.workingConfiguration };
    case EnvironmentActionTypes.ENVIRONMENT_RESET_CONFIGURATION:
      return { ...state, workingConfiguration: state.configuration };
    default:
      return state;
  }
}

const EnvironmentStore = createRxStore(
  'EnvironmentStore',
  initialState,
  reducer,
);

// SIDE EFFECT: when the store updates, update localstorage with the configuration stored inside
EnvironmentStore.pipe(distinctUntilChanged()).subscribe((newState) => {
  if (!IS_SENSEI_API_DEV_ENVIRONMENT_LOCKED) {
    saveEnvironmentToLocalStorage(newState.configuration);
  }
});

export default EnvironmentStore;
